import { DateUtility } from '@utility/date';
import { CommonUtility } from '@utility/common';
import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Cell, Checkbox, Row, Tooltip, cn } from '@skupreme/shared/ui';
import {
  canBatchOrder,
  canBatchWith,
  canFulfillOrder,
  ColumnHeader,
  FulfillmentCell,
  getFulfillmentIssues,
  OrderAgeCell,
  LocaleCell,
  OrderIdCell,
  OrdersTable,
  showsStageOn,
} from '@features/orders';
import type { ITableColumn } from 'types/common/table';
import {
  HideInvalidAddressOrderStatuses,
  OrderStatus,
  tooltipTexts,
} from '@utility/constants/common';
import useUrlPermission from '@hooks/url-permission';
import type { OrderNavigationState } from '@features/orders/lib/order-navigation';
import { useOrdersV2 } from '@features/orders/hooks/orders-v2';

/* EN-4544: the ID column carries the issue chips, so it takes a share from Fulfillment, whose label is short. */
const ORDER_COLUMN_WIDTHS = {
  orderId: 7,
  age: 2,
  fulfillment: 3,
  locale: 2,
  total: 2,
} as const;

export function OrderTime({ time, isEnabled }) {
  let timeColor = '';
  const noOfDays = DateUtility.noOfDays(time);
  if (isEnabled) {
    if (noOfDays < 1) {
      timeColor = 'var(--sk-success-fg)';
    } else if (noOfDays <= 2) {
      timeColor = 'var(--sk-warning-fg)';
    } else if (noOfDays <= 5) {
      timeColor = 'var(--sk-caution-fg)';
    } else if (noOfDays > 5) {
      timeColor = 'var(--sk-danger-fg)';
    }
  }
  return (
    <span style={timeColor ? { color: timeColor, fontWeight: 700 } : undefined}>
      {DateUtility.hourMinuteTime(time)}
    </span>
  );
}

function OrderRow({
  id,
  item,
  selectedOrder,
  toggleSelection,
  selectionMode,
  currency,
  checkboxFlag,
  validateAddress,
  isShipped,
  isFBA,
  status,
  orderIds,
  fulfillableOrderIds,
  listUrl,
}) {
  const { disabled, hidden } = useUrlPermission(`/app/orders/list/${status}`);
  /* EN-4117: fixing an address writes, which the new orders page does not do yet. */
  const { isV2 } = useOrdersV2();
  const navigate = useNavigate();

  const loading = useMemo(() => {
    if (item.updated) {
      return false;
    }
    return (
      item.loading ||
      item.status === 'pending' ||
      item.shipping_state === 'pending'
    );
  }, [item]);

  const fulfillmentIssues = useMemo(
    () =>
      getFulfillmentIssues({
        items: item.items,
        providers: item.fulfillment_providers,
        selectedFP: item.selectedFP,
        orderState: item.state,
        isShipped,
        highlightShortages: status === OrderStatus.PendingShipment,
        stockShortage: item.stock_shortage,
      }),
    [item, isShipped, status],
  );

  const batchProviderId = useMemo(() => {
    const first = Object.keys(selectedOrder || {})[0];
    return first ? selectedOrder[first] : undefined;
  }, [selectedOrder]);

  const isFulfillMode = selectionMode === 'fulfill';

  const canSelectRow = useMemo(() => {
    if (isFulfillMode) {
      return canFulfillOrder(item);
    }
    return !!selectedOrder?.[item._id] || canBatchWith(item, batchProviderId);
  }, [isFulfillMode, item, selectedOrder, batchProviderId]);

  /* Checkboxes exist only inside a selection mode, and each mode has its own rule. */
  const isEligible = isFulfillMode
    ? canFulfillOrder(item)
    : canBatchOrder(item);

  const selectionState =
    selectionMode && checkboxFlag && !hidden && isEligible
      ? {
          isSelected: !!(selectedOrder && selectedOrder[item._id]),
          canSelect: canSelectRow,
          isDisabled: disabled,
          onToggle: () => {
            if (canSelectRow) {
              toggleSelection(item);
            }
          },
        }
      : undefined;

  const isSelected = !!(selectedOrder && selectedOrder[item._id]);
  const isSettled =
    item.state === OrderStatus.Completed || item.virtuallyCompleted;
  const href = `/app/orders/${encodeURIComponent(item._id)}`;

  return (
    <Row
      id={id}
      href={href}
      onClick={event => {
        if (
          event.defaultPrevented ||
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        ) {
          return;
        }

        event.preventDefault();
        const state: OrderNavigationState = {
          orderIds,
          fulfillableOrderIds,
          listUrl,
        };
        navigate(href, { state });
      }}
      /* Selection is this list's own state, not React Aria's. */
      data-selected={isSelected || undefined}
      className={cn(
        'sk:cursor-pointer sk:align-middle',
        isSettled && 'sk:opacity-40',
      )}
    >
      <Cell>
        <OrderIdCell
          order={item}
          isDimmed={!!loading}
          isShipped={isShipped}
          showStage={showsStageOn(status, item)}
          extraIssues={fulfillmentIssues}
          notice={
            item.virtuallyCompleted
              ? 'A fulfillment request has been successfully initiated and awaiting tracking.'
              : undefined
          }
          selection={selectionState}
          addressFix={
            !isV2 &&
            item.invalid_address &&
            !HideInvalidAddressOrderStatuses.includes(status) &&
            !isShipped
              ? e => {
                  e.stopPropagation();
                  e.preventDefault();
                  validateAddress(item);
                }
              : undefined
          }
        />
      </Cell>
      <Cell>
        <OrderAgeCell
          acknowledgedAt={item.time_acknowledged}
          isCounting={
            item.state === OrderStatus.PendingShipment ||
            item.state === OrderStatus.PendingFulfillment
          }
          isProcessing={!!loading}
          shipBy={item.handling_time}
        />
      </Cell>
      <Cell>
        <FulfillmentCell
          items={item.items}
          providers={item.fulfillment_providers}
          selectedFP={item.selectedFP}
          highlightShortages={status === OrderStatus.PendingShipment}
          fallbackLabel={isFBA ? 'Amazon' : undefined}
        />
      </Cell>
      <Cell>
        <LocaleCell
          countryCode={item?.shipping_address?.countryCode}
          region={item?.shipping_address?.stateOrRegion}
        />
      </Cell>
      <Cell>
        {CommonUtility.currencyFormat(
          item.amount,
          currency || item.currency_code,
        )}
      </Cell>
    </Row>
  );
}

type OrderListRow = { _id: string };

export function OrderList({
  status,
  selectedOrder,
  selectionMode,
  loading,
  data,
  currentPage,
  totalPages,
  pageChanged,
  toggleSelection,
  pageSize,
  pageSizeChanged,
  totalData,
  sort,
  sortChanged,
  currency,
  error,
  search,
  allSelected,
  toggleAllSelection,
  validateAddress,
  isShipped = false,
  isFBA = false,
}) {
  const { disabled, hidden } = useUrlPermission();
  const { isV2 } = useOrdersV2();
  const location = useLocation();
  const orderIds = useMemo(
    () => (data || []).map(order => order._id).filter(Boolean),
    [data],
  );
  const fulfillableOrderIds = useMemo(
    () =>
      (data || [])
        .filter(canFulfillOrder)
        .map(order => order._id)
        .filter(Boolean),
    [data],
  );

  /* Carried into the details page so closing an order restores the same list. */
  const listUrl = `${location.pathname}${location.search}`;

  const message = useMemo(() => {
    if (error) {
      return error;
    }
    if (search) {
      return 'No Orders Found';
    }
    if (status === 'Pending Shipment') {
      return 'No Orders Pending Shipment';
    }
    if (status === 'Pending Fulfillment') {
      return 'No Orders Pending Fulfillment';
    }
    return 'No Orders Found';
  }, [status, error, search]);

  const columns = useMemo(() => {
    const showActions = status === OrderStatus.PendingShipment;

    const temp: ITableColumn[] = [
      {
        text: 'Order Id',
        key: 'order_id',
        textAlign: 'left',
        width: ORDER_COLUMN_WIDTHS.orderId,
        component: (
          <ColumnHeader
            label="ID"
            hint={tooltipTexts.orderId}
            prefix={
              selectionMode &&
              status === OrderStatus.PendingShipment &&
              CommonUtility.isValidArray(data) &&
              !hidden ? (
                <Tooltip
                  content={tooltipTexts.missingPermission}
                  placement="bottom start"
                  isDisabled={!disabled}
                >
                  <Checkbox
                    aria-label="Select all orders"
                    isSelected={allSelected}
                    isDisabled={disabled}
                    onChange={toggleAllSelection}
                  />
                </Tooltip>
              ) : null
            }
          />
        ),
      },
      {
        text: 'Order Age',
        key: 'order_date',
        textAlign: 'left',
        sortKey: 'time_acknowledged',
        width: ORDER_COLUMN_WIDTHS.age,
        component: <ColumnHeader label="Age" hint={tooltipTexts.orderAge} />,
      },
      {
        text: 'Fulfillment',
        key: 'fp',
        textAlign: 'left',
        width: ORDER_COLUMN_WIDTHS.fulfillment,
        component: (
          <ColumnHeader
            label="Fulfillment"
            hint={
              status === OrderStatus.PendingShipment
                ? tooltipTexts.fulfillmentProviderPending
                : tooltipTexts.fulfillmentProvider
            }
          />
        ),
      },
    ];

    temp.splice(3, 0, {
      text: 'Locale',
      key: 'locale',
      textAlign: 'left',
      width: ORDER_COLUMN_WIDTHS.locale,
      component: <ColumnHeader label="Locale" />,
    });

    temp.push({
      text: 'Total Price',
      key: 'total_price',
      textAlign: 'left',
      width: ORDER_COLUMN_WIDTHS.total,
      component: (
        <ColumnHeader label="Total Price" hint={tooltipTexts.totalPrice} />
      ),
    });

    return temp;
  }, [status, allSelected, data, selectionMode, disabled, hidden, isV2]);

  return (
    <OrdersTable<OrderListRow>
      data={data || []}
      currentPage={currentPage}
      totalPages={totalPages}
      totalData={totalData}
      pageChanged={pageChanged}
      renderRow={item => (
        <OrderRow
          key={item._id}
          id={item._id}
          item={item}
          isShipped={isShipped}
          currency={currency}
          selectedOrder={selectedOrder}
          toggleSelection={toggleSelection}
          selectionMode={selectionMode}
          checkboxFlag={status === OrderStatus.PendingShipment}
          validateAddress={validateAddress}
          isFBA={isFBA}
          status={status}
          orderIds={orderIds}
          fulfillableOrderIds={fulfillableOrderIds}
          listUrl={listUrl}
        />
      )}
      columns={columns}
      loading={loading}
      pageSizeChanged={pageSizeChanged}
      pageSize={pageSize}
      loadingRows={5}
      sort={sort}
      sortChanged={sortChanged}
      noDataMessage={message}
    />
  );
}
